import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import MetaTags from "react-meta-tags";
import { Row, Col, Text, SubTitle, Button, Title } from "../theme";
import BlocktoPlainText from "../utilities/BlocktoPlainText";
import BlockContent from "@sanity/block-content-to-react";
import MemeViewer from "./memeViewer";
import { useLocation, useHistory } from "react-router-dom";

const parseQuery = (search) => {
  const params = new URLSearchParams(search);
  return {
    tags: params.get("tags")?.split(",").filter(Boolean) || [],
    filters: params.get("filters")?.split(",").filter(Boolean) || [],
    category: params.get("category"),
    editions: params.get("editions")?.split(",").filter(Boolean) || [],
  };
};

const stringifyQuery = (tags, filters, category, editions) => {
  const params = new URLSearchParams();
  if (tags.length > 0) params.set("tags", tags.join(","));
  if (filters.length > 0) params.set("filters", filters.join(","));
  if (category) params.set("category", category);
  if (editions.length > 0) params.set("editions", editions.join(","));
  return `?${params.toString()}`;
};

const isFilterCategory = (categoryTitle) =>
  categoryTitle?.trim().toLowerCase() === "filters";

const sanitizeFilename = (value) =>
  (value || "selection")
    .toString()
    .trim()
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "selection";

const getImageExtension = (url, contentType) => {
  const typeExtension = contentType?.split("/")[1]?.split(";")[0];
  if (typeExtension) {
    return typeExtension === "jpeg" ? "jpg" : typeExtension;
  }

  const path = new URL(url).pathname;
  const extension = path.split(".").pop();
  return extension && extension !== path ? extension : "jpg";
};

const crcTable = (() => {
  const table = [];
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const getCrc32 = (bytes) => {
  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
};

const setUint16 = (view, offset, value) => {
  view.setUint16(offset, value, true);
};

const setUint32 = (view, offset, value) => {
  view.setUint32(offset, value, true);
};

const createZipBlob = (files) => {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach(({ name, bytes }) => {
    const nameBytes = encoder.encode(name);
    const crc = getCrc32(bytes);

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    setUint32(localView, 0, 0x04034b50);
    setUint16(localView, 4, 20);
    setUint16(localView, 8, 0);
    setUint32(localView, 14, crc);
    setUint32(localView, 18, bytes.length);
    setUint32(localView, 22, bytes.length);
    setUint16(localView, 26, nameBytes.length);
    localHeader.set(nameBytes, 30);

    localParts.push(localHeader, bytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    setUint32(centralView, 0, 0x02014b50);
    setUint16(centralView, 4, 20);
    setUint16(centralView, 6, 20);
    setUint16(centralView, 10, 0);
    setUint32(centralView, 16, crc);
    setUint32(centralView, 20, bytes.length);
    setUint32(centralView, 24, bytes.length);
    setUint16(centralView, 28, nameBytes.length);
    setUint32(centralView, 42, offset);
    centralHeader.set(nameBytes, 46);

    centralParts.push(centralHeader);
    offset += localHeader.length + bytes.length;
  });

  const centralOffset = offset;
  const centralSize = centralParts.reduce((size, part) => size + part.length, 0);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  setUint32(endView, 0, 0x06054b50);
  setUint16(endView, 8, files.length);
  setUint16(endView, 10, files.length);
  setUint32(endView, 12, centralSize);
  setUint32(endView, 16, centralOffset);

  return new Blob([...localParts, ...centralParts, endRecord], {
    type: "application/zip",
  });
};

const AllMemes = (props) => {
  const [settings, memes, tags, editions] = useSelector((state) => [
    state.data.settings,
    state.data.memes,
    state.data.tags,
    state.data.editions || [],
  ]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagStatus, setTagStatus] = useState(null);
  const [selectedAllCategory, setSelectedAllCategory] = useState(null);
  const [editionStatus, setEditionStatus] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const location = useLocation();
  const history = useHistory();

  // const [edition, setEdition] = useState(null);
  // const [amount, setAmount] = useState(3);
  const myRef = useRef();

  useEffect(() => {
    if (!tagStatus && tags && settings) {
      const selectedTags_ = [];

      settings.selectedTagsGrouped.forEach((group) => {
        const { selectedTags: groupedTags } = group;
        groupedTags.forEach(({ _ref }) => {
          const matchedTag = tags.find((tag) => tag._id === _ref);
          if (matchedTag) {
            selectedTags_.push({
              ...matchedTag,
              categoryTitle: group.categoryTitle,
            });
          }
        });
      });

      const ts = {};
      selectedTags_.forEach((tag) => {
        ts[tag.name] = false;
      });

      const es = {};
      editions.forEach((edition) => {
        es[edition.edition] = false;
      });

      const {
        tags: urlTags,
        filters: urlFilters,
        category: urlCategory,
        editions: urlEditions,
      } = parseQuery(location.search);

      [...urlTags, ...urlFilters].forEach((name) => {
        if (name in ts) ts[name] = true;
      });

      urlEditions.forEach((edition) => {
        if (edition in es) es[edition] = true;
      });

      const selectedCategory = selectedTags_.find(
        (tag) =>
          tag.categoryTitle === urlCategory &&
          !isFilterCategory(tag.categoryTitle)
      );

      setSelectedTags(selectedTags_);
      setTagStatus(ts);
      setSelectedAllCategory(selectedCategory?.categoryTitle || null);
      setEditionStatus(es);
    }
  }, [tags, settings, editions, tagStatus, location.search]);

  useEffect(() => {
    if (tagStatus && editionStatus) {
      const activeTags = Object.entries(tagStatus)
        .filter(([_, v]) => v)
        .map(([k]) => k);

      const activeFilters = activeTags.filter((name) => {
        const tag = selectedTags.find(
          (selectedTag) => selectedTag.name === name
        );
        return isFilterCategory(tag?.categoryTitle);
      });

      const activeCategoryTags = selectedAllCategory
        ? []
        : activeTags.filter((name) => {
            const tag = selectedTags.find(
              (selectedTag) => selectedTag.name === name
            );
            return !isFilterCategory(tag?.categoryTitle);
          });

      const activeEditions = Object.entries(editionStatus)
        .filter(([_, v]) => v)
        .map(([k]) => k);

      const newQuery = stringifyQuery(
        activeCategoryTags,
        activeFilters,
        selectedAllCategory,
        activeEditions
      );

      if (location.search !== newQuery) {
        history.replace({
          pathname: location.pathname,
          search: newQuery,
        });
      }
    }
  }, [
    tagStatus,
    editionStatus,
    selectedTags,
    selectedAllCategory,
    history,
    location,
  ]);

  const selectedMemes = useMemo(() => {
    if (!memes || !tagStatus || !editionStatus) {
      return [];
    }

    const activeTags = Object.entries(tagStatus)
      .filter(([_, v]) => v)
      .map(([k]) => k);

    const activeFilters = activeTags.filter((name) => {
      const tag = selectedTags.find((selectedTag) => selectedTag.name === name);
      return isFilterCategory(tag?.categoryTitle);
    });

    const activeCategoryTags = activeTags.filter((name) => {
      const tag = selectedTags.find((selectedTag) => selectedTag.name === name);
      return !isFilterCategory(tag?.categoryTitle);
    });

    const hasEditionSelection = Object.values(editionStatus).some(Boolean);
    const hasTaxonomySelection =
      activeTags.length > 0 || Boolean(selectedAllCategory);

    return memes.filter((m) => {
      if (!m.display || !m.img) {
        return false;
      }

      if (hasEditionSelection && !editionStatus[m.edition]) {
        return false;
      }

      if (!hasTaxonomySelection) {
        return true;
      }

      if (!m.taxonomy) {
        return false;
      }

      const memeTags = m.taxonomy.map((t) => t.name);
      const filterMatch = activeFilters.every((tag) =>
        memeTags.includes(tag)
      );
      const categoryMatch = selectedAllCategory
        ? selectedTags
            .filter((tag) => tag.categoryTitle === selectedAllCategory)
            .some((tag) => memeTags.includes(tag.name))
        : activeCategoryTags.every((tag) => memeTags.includes(tag));

      return filterMatch && categoryMatch;
    });
  }, [memes, tagStatus, editionStatus, selectedTags, selectedAllCategory]);

  const selectionName = useMemo(() => {
    if (!tagStatus || !editionStatus) {
      return "selection";
    }

    const activeTags = Object.entries(tagStatus)
      .filter(([_, v]) => v)
      .map(([k]) => k);

    const activeFilters = activeTags.filter((name) => {
      const tag = selectedTags.find((selectedTag) => selectedTag.name === name);
      return isFilterCategory(tag?.categoryTitle);
    });

    const activeCategoryTags = selectedAllCategory
      ? [`all-${selectedAllCategory}`]
      : activeTags.filter((name) => {
          const tag = selectedTags.find(
            (selectedTag) => selectedTag.name === name
          );
          return !isFilterCategory(tag?.categoryTitle);
        });

    const activeEditions = Object.entries(editionStatus)
      .filter(([_, v]) => v)
      .map(([k]) => `edition-${k}`);

    return sanitizeFilename(
      [...activeCategoryTags, ...activeFilters, ...activeEditions].join("-") ||
        "all-memes"
    );
  }, [tagStatus, editionStatus, selectedTags, selectedAllCategory]);

  const downloadSelection = async () => {
    if (selectedMemes.length === 0 || downloadStatus) {
      return;
    }

    setDownloadStatus("preparing");

    try {
      const files = await Promise.all(
        selectedMemes.map(async (m, i) => {
          const response = await fetch(m.img);
          if (!response.ok) {
            throw new Error(`Could not download ${m.img}`);
          }

          const bytes = new Uint8Array(await response.arrayBuffer());
          const extension = getImageExtension(
            m.img,
            response.headers.get("content-type")
          );
          const description = sanitizeFilename(m.description || m._id);
          const edition = m.edition ? `-${sanitizeFilename(m.edition)}` : "";

          return {
            name: `${String(i + 1).padStart(3, "0")}-${description}${edition}.${extension}`,
            bytes,
          };
        })
      );

      const zip = createZipBlob(files);
      const url = URL.createObjectURL(zip);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectionName}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      window.alert("Could not create the zip download. Please try again.");
    } finally {
      setDownloadStatus(null);
    }
  };

  if (!settings || !memes || !tagStatus || !editionStatus) {
    return null;
  }

  const getMemes = () => {
    return (
      <>
        <MemeViewer
          amount={3}
          auto
          list={selectedMemes.map((m) => ({ _ref: m._id }))}
        />
      </>
    );
  };

  return (
    <>
      <MetaTags>
        <title>{`${settings.title} - Memes`}</title>
        <meta name="description" content={BlocktoPlainText(settings.intro)} />
        <meta property="og:title" content={`${settings.title}`} />
        <meta property="og:image" content={settings.url + "?w=720"} />
      </MetaTags>

      <Row center="xs">
        <Col xs={12} sm={12} md={false}>
          <br />
          <br />
        </Col>
        <Col xs={12}>
          <div style={{ textAlign: "center" }}>
            <Title>MEMES</Title>
          </div>
        </Col>
      </Row>

      <Row>
        <Col
          xs={false}
          sm={false}
          md={5}
          style={{ transform: "translateY(-35px)" }}
        >
          {getMemes()}
        </Col>
        <Col xs={12} sm={12} md={7}>
          <Text>
            <BlockContent blocks={settings.allmemes} />
          </Text>

          {/* <br />
          <Title>EDITIONS</Title>
          {[...editions]
            .sort((a, b) => Number(a.edition) - Number(b.edition))
            .map(({ edition }) => (
            <Button
              key={edition}
              noUrl
              text={edition}
              invert={!editionStatus?.[edition]} // inverts the style when not active
              action={(_, text) => {
                const newStatus = {};
                editions.forEach(({ edition }) => {
                  newStatus[edition] = edition === text;
                });
                setEditionStatus(newStatus);
              }}
            />
          ))}

          <br /> */}
          <br />

          <Button
            key="download-selection"
            noUrl
            text={`download selection (n=${selectedMemes.length})`}
            invert={selectedMemes.length === 0 || Boolean(downloadStatus)}
            action={downloadSelection}
          />

          <br />
          <br />

          <Title>CATEGORIES</Title>
          {[...new Set(selectedTags.map((t) => t.categoryTitle))].map(
            (catTitle) => (
              <div key={catTitle}>
                <SubTitle>{catTitle}</SubTitle>
                {!isFilterCategory(catTitle) && (
                  <Button
                    key={`all-${catTitle}`}
                    noUrl
                    text={`All ${catTitle}`}
                    invert={selectedAllCategory !== catTitle}
                    action={() => {
                      const isActive = selectedAllCategory === catTitle;
                      const nts = {};

                      selectedTags.forEach((tag) => {
                        nts[tag.name] =
                          isFilterCategory(tag.categoryTitle) &&
                          tagStatus[tag.name];
                      });

                      setTagStatus(nts);
                      setSelectedAllCategory(isActive ? null : catTitle);
                    }}
                  />
                )}
                {selectedTags
                  .filter((tag) => tag.categoryTitle === catTitle)
                  .map(({ _id, name, categoryTitle }) => (
                    <Button
                      key={_id}
                      noUrl
                      text={name}
                      invert={!tagStatus?.[name]} // active when true
                      action={(_, text) => {
                        const nts = {};
                        const clickedIsFilter = isFilterCategory(categoryTitle);
                        const clickedIsActive = tagStatus[text];

                        selectedTags.forEach((tag) => {
                          if (clickedIsFilter) {
                            nts[tag.name] =
                              tag.name === text
                                ? !clickedIsActive
                                : tagStatus[tag.name];
                            return;
                          }

                          const tagIsActiveFilter =
                            isFilterCategory(tag.categoryTitle) &&
                            tagStatus[tag.name];

                          nts[tag.name] =
                            tagIsActiveFilter
                              ? true
                              : tag.name === text && !clickedIsActive;
                        });
                        if (!clickedIsFilter) {
                          setSelectedAllCategory(null);
                        }
                        setTagStatus(nts);
                      }}
                    />
                  ))}
                <br />
              </div>
            )
          )}

          <br />
          <br />
          <Title>EDITIONS</Title>

          <Button
            key="all-editions"
            noUrl
            text="All Editions"
            invert={Object.values(editionStatus || {}).some((v) => v)} // highlighted when any filter is active
            action={() => {
              const resetStatus = {};
              editions.forEach(({ edition }) => {
                resetStatus[edition] = false;
              });
              setEditionStatus(resetStatus);
            }}
          />

          {[...editions]
            .sort((a, b) => Number(a.edition) - Number(b.edition))
            .map(({ edition }) => (
              <Button
                key={edition}
                noUrl
                text={edition}
                invert={!editionStatus?.[edition]} // inverts the style when not active
                action={(_, text) => {
                  const newStatus = {};
                  editions.forEach(({ edition }) => {
                    newStatus[edition] =
                      edition === text ? !editionStatus[edition] : false;
                  });
                  setEditionStatus(newStatus);
                }}
              />
            ))}
        </Col>
        <Col xs={12} sm={12} md={false}>
          <br />
          <br />
          {getMemes()}
        </Col>
      </Row>
    </>
  );
};

export default AllMemes;
